import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useSettings } from "@/context/AppContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/rice", label: "Rice" },
  { to: "/spices", label: "Spices" },
  { to: "/tea", label: "Tea" },
  { to: "/quality", label: "Quality" },
  { to: "/markets", label: "Export Markets" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { settings } = useSettings();
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const brand = settings?.business?.brand || "AnVibes™";
  const name = settings?.business?.public_name || "AnVibes Exports";
  const inv = settings?.invocation || { enabled: true, devanagari: "श्री मात्रे नमः", english: "Shri Maatre Namah" };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-500 ${
        scrolled ? "bg-forest/90 border-white/10" : "bg-forest/60 border-white/5"
      }`}
    >
      {inv.enabled !== false && (inv.devanagari || inv.english) && (
        <div
          data-testid="header-invocation"
          className="flex h-8 items-center justify-center gap-3 border-b border-gold/20 bg-forest-deep/80 px-4 text-center"
        >
          <span className="text-gold/60 text-xs">॥</span>
          {inv.devanagari && (
            <span className="font-serif text-sm sm:text-[15px] tracking-wide text-gold-light" data-testid="invocation-devanagari">{inv.devanagari}</span>
          )}
          {inv.devanagari && inv.english && <span className="h-3 w-px bg-gold/30" />}
          {inv.english && (
            <span className="font-serif text-[12px] sm:text-[13px] tracking-[0.2em] text-gold/90" data-testid="invocation-english">{inv.english}</span>
          )}
          <span className="text-gold/60 text-xs">॥</span>
        </div>
      )}
      <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-4 sm:px-8 lg:px-16">
        <Link to="/" className="group flex flex-col leading-none" data-testid="header-logo">
          <span className="font-serif text-2xl sm:text-[1.7rem] font-semibold text-parchment tracking-tight">
            {brand.replace("™", "")}
            <sup className="text-gold text-xs align-super">™</sup>
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-gold/90 mt-1">{name}</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors duration-300 hover:text-gold-light ${
                  isActive ? "text-gold-light" : "text-parchment/75"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/quote"
            data-testid="header-quote-cta"
            className="group hidden sm:inline-flex items-center gap-2 bg-gold px-5 py-2.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-forest transition-colors duration-300 hover:bg-gold-light"
          >
            Request a Quote
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-parchment p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-forest-deep/95 backdrop-blur-xl" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-6 gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 text-lg font-serif border-b border-white/5 ${isActive ? "text-gold-light" : "text-parchment/85"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/quote"
              data-testid="mobile-quote-cta"
              className="mt-5 inline-flex items-center justify-center gap-2 bg-gold px-5 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-forest"
            >
              Request a Quote <ArrowUpRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
